import { EmptyState } from "@uzverse/ui";
import { useTranslation } from "react-i18next";
import { TaskChip, TaskProgress, useCommonT } from "../components/common.js";
import type { TaskItem } from "../state/store.js";
import { TERMINAL_STATES } from "../state/store.js";
import { useAppState } from "../state/useAppState.js";

function TaskRow({ task }: { task: TaskItem }) {
  const { t } = useTranslation("web");
  const title = task.text || t("task.untitled");
  return (
    <li className="card task">
      <h3>{title}</h3>
      <div className="row">
        <TaskChip state={task.state} />
        {task.detail && <span className="muted small">{task.detail}</span>}
      </div>
      <TaskProgress state={task.state} label={t("task.progress", { text: title })} />
      {task.artifacts.length > 0 && (
        <ul className="plain" aria-label={t("tasks.artifacts")}>
          {task.artifacts.map((a) => (
            <li key={a.path}>
              <a className="link" href={a.url ?? "#"} target="_blank" rel="noreferrer noopener">
                {a.path}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function TasksPage() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  const { tasks } = useAppState();
  const all = Object.values(tasks).sort((a, b) => b.updatedAt - a.updatedAt);
  const active = all.filter((x) => !TERMINAL_STATES.includes(x.state));
  const finished = all.filter((x) => TERMINAL_STATES.includes(x.state));
  return (
    <section aria-labelledby="tasks-title" className="stack">
      <h1 id="tasks-title">{t("tasks.title")}</h1>
      {all.length === 0 && <EmptyState t={tc} title={t("tasks.empty")} />}
      {active.length > 0 && (
        <>
          <h2>{t("tasks.active")}</h2>
          <ul className="plain stack" aria-live="polite">
            {active.map((x) => (
              <TaskRow key={x.id} task={x} />
            ))}
          </ul>
        </>
      )}
      {finished.length > 0 && (
        <>
          <h2>{t("tasks.finished")}</h2>
          <ul className="plain stack">
            {finished.map((x) => (
              <TaskRow key={x.id} task={x} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
